/* eslint-disable @typescript-eslint/consistent-type-assertions */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { Box, Grid, IconButton, Typography } from '@mui/material'
import 'core/styles.css'
import { ReportedIncome } from 'Components/ChildDetails/ReportedIncome'
import { ProgramAllowedYear } from 'Components/Program/ProgramAllowedYear'
import AlertMessage from 'Components/AlertMessage'
import { type DocumentCaregiver, type DocumentData, DocumentInfo, type DocumentUpdateModel } from 'Components/DocumentList/DocumentInfo'
import { DocumentTarget, type lookup, type ProgramVerificationDetails } from 'core/types'
import { GeneralDocumentUploader } from 'Pages/Referral/GeneralDocuments'
import { CheckBoxWithLabel } from 'Components/CheckBox'
import { type DocumentFile } from 'Components/Program/ProgramVerificationDetails'
import { useState } from 'react'
import { useAuth } from 'hooks/use-auth'
import { LabelWithToolTip } from 'Components/Label/Index'
import { FormatCurrency } from 'core/Utilities'
import { Edit } from '@mui/icons-material'
import { TextFieldWithLabel } from 'Components/TextField'
import CheckIcon from '@mui/icons-material/Check'
import DeleteIcon from '@mui/icons-material/Delete'
import DescriptionIcon from '@mui/icons-material/Description'
export function IncomeVerification (props: {
  data: ProgramVerificationDetails
  disabled?: boolean
  setFiles: (files: DocumentFile[]) => Promise<void>
  files: DocumentFile[]
  caregivers: lookup[]
  years: number[]
  incomeDiscrepencyApprovalChanged: (newVal: boolean) => Promise<void>
  updateMembers: (val: number) => Promise<void>
}): JSX.Element {
  const auth = useAuth()
  const [index, setIndex] = useState(-1)
  const [showEditMembers, setShowEditMembers] = useState(false)
  const [verifiedMembers, setVerifiedMembers] = useState(props.data.memberCount)
  const verifiedIncome = props.files.reduce((accumulator, doc) => accumulator + Number((doc.doc.incomeAmount ?? 0)), 0)
  let calculatedFPL = (props.data.baseFPL ?? 0) + ((props.data.memberCount - 1) * (props.data.additionalPerMember ?? 0))
  if (calculatedFPL === 0) {
    calculatedFPL = 1
  }
  const verifiedPercent = (Math.round((verifiedIncome / (calculatedFPL)) * 100))
  const handleDocAttached = async (existingDocuments: DocumentData[]): Promise<void> => {
    const newAttachedFiles = existingDocuments.map(d => {
      const x: DocumentFile = { doc: d, file: undefined, editMode: false, documentTypeId: props.data.requiredDocType ?? 0 }
      return x
    }
    )
    const x = [...props.files, ...newAttachedFiles]
    void props.setFiles(x)
  }
  const handleDocUpload = async (file: File): Promise<void> => {
    if (file !== null) {
      const cgs: DocumentCaregiver[] = props.caregivers.map(c => {
        return {
          caregiverID: c.id,
          caregiverName: c.name,
          selected: false
        }
      })
      const newFile: DocumentData = {
        documentTypeID: props.data.requiredDocType ?? 0,
        documentTarget: DocumentTarget.ADULT,
        incomeRelated: true,
        caregivers: cgs,
        fileName: file.name,
        documentID: index,
        verified: false
      } as DocumentData
      setIndex(index - 1)
      void props.setFiles([...props.files, { doc: newFile, file, editMode: true, documentTypeId: newFile.documentTypeID }])
    }
  }

  const handleVerifyChange = async (docId: number, verifyMode: boolean): Promise<void> => {
    const newFileList = [...props.files]
    const updatedFile = newFileList.filter(f => f.doc.documentID === docId)[0]
    updatedFile.editMode = verifyMode
    await props.setFiles(newFileList)
  }
  const handleDocVerified = async (docId: number, data: DocumentUpdateModel): Promise<void> => {
    const newFileList = [...props.files]
    const updatedFile = newFileList.filter(f => f.doc.documentID === docId)[0]
    updatedFile.doc.caregivers.forEach(element => {
      element.selected = data.caregivers.some(d => d === element.caregiverID)
    })
    updatedFile.editMode = false
    updatedFile.doc.verified = true
    updatedFile.doc.incomeAmount = data.incomeAmount
    updatedFile.doc.incomeYear = data.incomeYear
    updatedFile.doc.verifiedBy = (auth.user?.firstName ?? '') + ' ' + (auth.user?.lastName ?? '')
    updatedFile.doc.verifiedDate = new Date()
    updatedFile.doc.verificationNote = data.verificationNote
    await props.setFiles(newFileList)
  }
  const handleDocUnverified = async (docId: number): Promise<void> => {
    const newFileList = [...props.files]
    const updatedFile = newFileList.filter(f => f.doc.documentID === docId)[0]
    updatedFile.doc.verified = false
    await props.setFiles(newFileList)
  }
  const handleDelete = async (docId: number): Promise<void> => {
    const newFileList = [...props.files].filter(f => f.doc.documentID !== docId)
    await props.setFiles(newFileList)
  }
  const updateMembers = async (): Promise<void> => {
    await props.updateMembers(verifiedMembers)
    setShowEditMembers(false)
  }
  const cancelUpdateMembers = async (): Promise<void> => {
    setVerifiedMembers(props.data.memberCount)
    setShowEditMembers(false)
  }
  const hasNoApprovals = props.data.incomeApprovals?.length === 0
  const meetsOverIncomeCutoff = props.data.incomeCutoff != null && (verifiedPercent > props.data.incomeCutoff)
  const meetsPrioCutoff = props.data.prioritizedProgramCutoff != null && (verifiedPercent < props.data.prioritizedProgramCutoff)
  const isAutoWaiverEligible = props.data.autoWaiver
  const hasDocs = props.files.length > 0
  const disabled = props.data.status === 'Verified' || props.disabled
  return <Box className='CardDisplay'>
    <Typography variant='h6' sx={{ fontWeight: 'bold' }} gutterBottom>Income Details</Typography>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} lg={5}>
        <LabelWithToolTip sx={{ fontWeight: 'bold', mb: '10px', alignContent: 'center' }} labelText='Reported Income' toolTipText='This is a snapshot of the income reported at the time this program verification was created.' />
        <ReportedIncome id={props.data?.reportedIncomeID} canEdit={false} childID={props.data.childID} />
      </Grid>
      <Grid item xs={12} sm={6} lg={5}>
        <LabelWithToolTip sx={{ fontWeight: 'bold', width: 'max-content', mb: '10px', alignContent: 'center' }} labelText='Allowed Years' toolTipText={`These are the years that are allowed for income verification by the system for the ${props.data.timeSpanName} time period.`} />
        <Box sx={{ display: 'flex', flexDirection: 'row', flexBasis: '50%', flexGrow: 0, flexShrink: 0, gap: 2, flexWrap: 'wrap' }}>
          {props.years?.map(y => <ProgramAllowedYear text={y.toString()} />)}
        </Box>
      </Grid>
    </Grid>
    <Box pb={'40px'}>
      <Typography variant='h6' sx={{ fontWeight: 'bold' }} gutterBottom>Income Documents</Typography>
      <Grid container width={'100%'} sx={{ justifyItems: 'center', gap: 2, justifyContent: 'center' }}>

        {props.files.map(d => {
          const data: DocumentData = d.doc
          return <Grid item sx={{ justifyItems: 'center', width: '-webkit-fill-available' }}>
            <DocumentInfo
              yearOptions={props.years}
              verifyMode={d.editMode}
              setVerifyMode={handleVerifyChange}
              onVerify={handleDocVerified}
              onUnVerify={handleDocUnverified}
              onDelete={handleDelete}
              document={data} disabled={disabled} documentID={data.documentID} childID={props.data.childID}
            />
          </Grid>
        })}
        {props.files.length === 0 && (<Box display={'flex'} flexDirection={'column'} alignItems={'center'} >
          <DescriptionIcon color='primary' sx={{ width: '60px', height: '60px' }} />
          <Typography>No Documents Uploaded</Typography>
        </Box>
        )}
      </Grid>
    </Box>
    <Box >
      <Box display={'flex'} flexDirection={'row'} sx={{ alignItems: 'center' }}>
        <Typography fontWeight={'bold'}>Verified Members: {props.data.memberCount}</Typography>
        {!showEditMembers && (
          <IconButton sx={{ ml: '0 !important' }} size='small' color='inherit' onClick={() => { setShowEditMembers(!showEditMembers) }}>
            <Edit fontSize='small' height={'15px'} width={'15px'} />
          </IconButton>
        )}
        {showEditMembers &&
          <>
            <IconButton sx={{ ml: '0 !important' }} size='small' color='inherit' onClick={updateMembers}>
              <CheckIcon fontSize='small' height={'15px'} width={'15px'} />
            </IconButton>
            <TextFieldWithLabel
              name={'verifiedMembers'}
              type='number'
              min={0}
              onChange={(e) => { setVerifiedMembers(Number(e.target.value)) }}
              value={verifiedMembers?.toString() ?? ''}
            />

            <IconButton sx={{ ml: '0 !important' }} size='small' color='inherit' onClick={cancelUpdateMembers}>
              <DeleteIcon fontSize='small' height={'15px'} width={'15px'} />
            </IconButton>
          </>
        }
      </Box>
      <Typography fontWeight={'bold'}>Total verified income: {FormatCurrency(verifiedIncome)}</Typography>
      <Typography fontWeight={'bold'}>Verified FPL: {verifiedPercent}%</Typography>
    </Box>
    {!disabled && props.data.requiredDocType != null && (
      <Box>
        <GeneralDocumentUploader childID={props.data?.childID ?? 0}
          documentTypeId={props.data.requiredDocType}
          documentTarget={props.data.requiredDocTarget}
          childName={props.data.childName}
          readOnly={false}
          years={props.years}
          onDocumentUploaded={handleDocUpload}
          onDocumentAttach={handleDocAttached}
          referenceType={'programVerification'}
          referenceId={props.data.id}
          incomeRelated={true}
        />
      </Box>
    )}
    {!isAutoWaiverEligible &&
    hasNoApprovals && hasDocs &&
    meetsOverIncomeCutoff &&
    (
      <Box sx={{ py: '20px' }}>
        <AlertMessage message={`Based on the Verified FPL, this verification requires an over income approval.  This over income request requires an approval by ${props.data.isdName ?? ''}.  This verification will have a status of Over Income Pending until that request is approved`} type={'warning'} />
      </Box>
    )}
    { (hasNoApprovals && hasDocs &&
    (isAutoWaiverEligible ||
    meetsPrioCutoff)) &&
    (
      <Box sx={{ py: '20px' }}>
        <AlertMessage message={`Based on the Verified FPL, this verification requires a ${props.data.prioritizedProgramName ?? ''} priority waiver. This priority waiver request requires an approval. This verification will have a status of Waiver Pending until that request is approved.`} type={'warning'} />
      </Box>
    )}
    { !hasNoApprovals && hasDocs &&
    !(meetsOverIncomeCutoff || meetsPrioCutoff) &&
    (
      <Box sx={{ py: '20px' }}>
        <AlertMessage message={'The changes you have made will result in the approval being cancelled as this child no longer meets the criteria for an approval'} type={'warning'} />
      </Box>
    )}
    {(verifiedIncome !== props.data.annualIncome) && hasDocs &&
    (
      <Box>
        <AlertMessage message={'The verified and reported income do not match. You will be allowed to save, but you will need to verify this discrepency is correct before you can advance the status of this approval.'} type={'warning'} />
        <CheckBoxWithLabel name='differenceCheckbox' edge='start' label='Acknowledge Difference Between Reported and Verified Income'
          value={props.data.acknowledgeIncomeGap ?? ''} disabled={disabled} setValue={props.incomeDiscrepencyApprovalChanged}
        />
      </Box>
    )}

  </Box>
}
